import React, { useContext } from 'react';

import { Box, Flex, Inline, Stack } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { fg } from '@confluence/feature-gating';
import { ExperienceTrackerContext, AI_AUDIO_BRIEFING } from '@confluence/experience-tracker';

import { useAudioBriefing } from '../../../../hooks/useAudioBriefing';
import { PlayPauseButton } from '../../../controls/PlayPauseButton';
import { PlayFromBeginningButton } from '../../../controls/PlayFromBeginningButton';
import { SkipBackButton } from '../../../controls/SkipBackButton';
import { SkipForwardButton } from '../../../controls/SkipForwardButton';
import { PlaybackSpeedControl } from '../../../controls/PlaybackSpeedControl';
import { AudioTimeIndicator } from '../../../controls/AudioTimeIndicator';

type AudioBriefingPopupListenPanelProps = {
	audioRef: React.RefObject<HTMLAudioElement>;
};

const styles = cssMap({
	wrapper: {
		flex: 1,
	},
	outerControlButtonWrapper: {
		// This is the width of the speed option that requires the most space
		// Ensures that the displayed speed is never truncated and that spacing remains uniform
		width: '54px',
	},
});

export const AudioBriefingPopupListenPanel = ({ audioRef }: AudioBriefingPopupListenPanelProps) => {
	const [{ isPlaying, isLoading, hasFetched, activeContent }, { fetchBriefing }] =
		useAudioBriefing();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const { trackAIInteractionInit } = useAIEventsInstrumentation();

	const handleFetchContent = () => {
		experienceTracker.start({
			name: AI_AUDIO_BRIEFING,
			attributes: {
				contentId: activeContent?.id,
			},
		});
		fetchBriefing();
		trackAIInteractionInit();
	};

	const shouldDisableControls = isLoading || !hasFetched;

	return (
		<Box paddingBlock="space.100" xcss={styles.wrapper} testId="listen-panel-controls">
			<Stack space="space.150" alignInline="center">
				<Inline space="space.150" alignBlock="center">
					{fg('confluence_briefings_playback_speed') && (
						<Flex xcss={styles.outerControlButtonWrapper} justifyContent="center">
							<PlayFromBeginningButton audioRef={audioRef} isDisabled={shouldDisableControls} />
						</Flex>
					)}

					<SkipBackButton audioRef={audioRef} isDisabled={shouldDisableControls} />
					<PlayPauseButton
						audioRef={audioRef}
						isPlaying={isPlaying}
						isLoading={isLoading}
						hasFetched={hasFetched}
						fetchContent={handleFetchContent}
					/>
					<SkipForwardButton audioRef={audioRef} isDisabled={shouldDisableControls} />

					{fg('confluence_briefings_playback_speed') && (
						<Box xcss={styles.outerControlButtonWrapper}>
							<PlaybackSpeedControl audioRef={audioRef} isDisabled={shouldDisableControls} />
						</Box>
					)}
				</Inline>
				<AudioTimeIndicator audioRef={audioRef} isLoading={isLoading} hasFetched={hasFetched} />
			</Stack>
		</Box>
	);
};
